import React, {
  ReactNode,
  useState,
  createContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react'
import moment from 'moment-timezone'

import {
  append,
  dec,
  evolve,
  find,
  findIndex,
  inc,
  lensPath,
  map,
  over,
  pipe,
  propEq,
  remove,
  sum,
  isEmpty,
  filter,
  reduce,
} from 'ramda'

import { type PurchaseItem } from '../../PurchasesApi'
import convertOrderToPost from './convertOrderToPost'

/**
 *   ЗАКАЗ
 */

export type OrderDataItemType = {
  guid: string
  n: number
  cost: number
  type: 0 | 1 | 'item' | 'complex' // 0 -- блюдо, 1 -- комплект
  title?: string
  portion?: string
  free_for_sale: boolean
}

export type OrderType = {
  customer_id?: number
  sale_point_id?: number
  building_id?: number
  time?: string
  guid?: string
  status?: 0
  items?: Array<PurchaseItem>
}

export type OrderDataContextType = {
  order: OrderType
  orderTotal: number
  orderTotalConsideringFreeItems: number
  modified: boolean
  someItemsRemoved: boolean
  somePricesChanged: boolean
  countByCategoryId: { [key: string]: number }
  addToOrder: (item: OrderDataItemType) => void
  setOrder: (newOrder: OrderType) => void
  initOrder: (newOrder: OrderType) => void
  isItemInOrder: (guid: string) => boolean
  itemCountIncrement: (guid: string) => void
  itemCountDecrement: (guid: string) => void
  itemRemove: (guid: string) => void
  savedOrder: OrderType
  additionalFreeGuids: { [guid: string]: boolean }
  setAdditionalFreeGuids: (
    x:
      | ((o: { [guid: string]: boolean }) => { [guid: string]: boolean })
      | { [guid: string]: boolean },
  ) => void
}

export const OrderDataContext = createContext<OrderDataContextType>({
  order: {},
  orderTotal: 0,
  orderTotalConsideringFreeItems: 0,
  modified: false,
  someItemsRemoved: false,
  somePricesChanged: false,
  countByCategoryId: {},
  addToOrder: (item: OrderDataItemType) => {},
  setOrder: (newOrder: OrderType) => {},
  initOrder: (newOrder: OrderType) => {},
  isItemInOrder: (guid: string) => false,
  itemCountIncrement: (guid: string) => {},
  itemCountDecrement: (guid: string) => {},
  itemRemove: (guid: string) => {},
  savedOrder: {},
  additionalFreeGuids: {},
  setAdditionalFreeGuids: () => {},
})

type Props = {
  children: ReactNode | ((contextValue: OrderDataContextType) => ReactNode)
  personId: number
  date: string
  dishes: { [key: string]: any }
  complexes: { [key: string]: any }
}

const OrderData = ({ children, personId, date, dishes, complexes }: Props) => {
  const lockControls = useRef(false)

  // текущий заказ, с которым идет работа
  const [state, setState] = useState<OrderType>({})
  // заказ, сохраненный, чтоб откатиться
  const [savedOrder, saveOrder] = useState<OrderType>({})
  // хранение бесплатных guid товаров из заказа (некоторые бесплатные товары, используемые в заказе, становятся платными)
  const [additionalFreeGuids, setAdditionalFreeGuids] = useState<{ [guid: string]: boolean }>({})
  // флаг изменения заказа
  const [modified, setModified] = useState(false)
  // флаг - некоторые товары были удалены и больше не доступны в заказе
  const [someItemsRemoved, setSomeItemsRemoved] = useState(false)
  // флаг - некоторые цены на товары в заказе были изменены
  const [somePricesChanged, setSomePricesChanged] = useState(false)
  // текущая дата еще не наступила (отдельный механизм создания предварительного заказа)
  const isDateInFuture = useMemo(() => moment(date).isAfter(moment(), 'day'), [date])

  // dishes, complexes
  // нужно знать текущие доступные блюда и комплекты:
  // 1. чтоб определить изменения в элементах заказа (соответствие их текущим блюдам/комплектам) -- initOrder
  // 2. чтоб определить количество блюд из каждой категории, которые сейчас в заказе --

  /**
   *  Инициализация заказа
   *  (флаг изменения данных сбрасывается, блюда сверяются по цене и наличию с доступными)
   */

  const initOrder = useCallback(
    (newOrder) => {
      if (!newOrder) return
      setModified(false)
      setSomeItemsRemoved(false)
      setSomePricesChanged(false)
      const verifiedItems =
        newOrder.items &&
        pipe(
          // проверка доступности товара
          filter(({ guid, type }) => {
            const isAvailable = !!(type === 'complex' || type === 1
              ? complexes[guid] && complexes[guid].available
              : dishes[guid] && dishes[guid].available)
            if (!isAvailable) {
              setSomeItemsRemoved(true)
              return false
            }
            return true
          }),
          // проверка цены товара
          map(({ guid, cost, type, ...others }: any) => {
            const origin = type === 'complex' || type === 1 ? complexes[guid] : dishes[guid]
            if (origin.cost !== cost) {
              setSomePricesChanged(true)
            }
            return { guid, cost: origin.cost, type, ...others }
          }),
        )(newOrder.items)

      const converted = convertOrderToPost(
        { ...newOrder, items: verifiedItems },
        personId,
        isDateInFuture ? date : undefined,
      )

      setState(converted)
      saveOrder({ ...converted })
    },
    [setState, personId, date, isDateInFuture, complexes, dishes],
  )

  /**
   *  Инициализация заказа объектом с данными заказа
   */

  const setOrder = useCallback(
    (newOrder) => {
      setState(convertOrderToPost(newOrder, personId, isDateInFuture ? date : undefined))
      setModified(true)
    },
    [setState, personId, date, isDateInFuture],
  )

  /**
   * Еффект обнуления флага изменения, если заказ пустой
   */

  useEffect(() => {
    if (isEmpty(state) || !state.items || isEmpty(state.items)) {
      setModified(false)
    }
  }, [state])

  /**
   *  Добавление в заказ
   */

  const addToOrder = useCallback(
    (item) => {
      setModified(true)
      setState(evolve({ items: append(item) }))
    },
    [setState],
  )

  /**
   *  Проверка есть ли товар в заказе
   */

  const isItemInOrder = useCallback(
    (guid) => !!(state.items && find(propEq('guid', guid), state.items)),
    [state],
  )

  /**
   *  Увеличение количества блюда/комплекта в заказе
   */

  const itemCountIncrement = useCallback(
    (guid) => {
      if (lockControls.current) return
      lockControls.current = true

      if (!state.items) return
      const index = findIndex(propEq('guid', guid), state.items)
      if (index < 0) return
      setModified(true)
      setState(over(lensPath(['items', index, 'n']), inc))
    },
    [state.items, setState],
  )

  /**
   *  Уменьшение количества блюда/комплекта в заказе
   */

  const itemCountDecrement = useCallback(
    (guid) => {
      if (lockControls.current) return
      lockControls.current = true

      if (!state.items) return
      const index = findIndex(propEq('guid', guid), state.items)
      if (index < 0) return
      setModified(true)
      setState(over(lensPath(['items', index, 'n']), dec))
    },
    [state.items, setState],
  )

  /**
   *  Удаление из заказа по guid
   */

  const itemRemove = useCallback(
    (guid) => {
      if (!state.items) return
      const index = findIndex(propEq('guid', guid), state.items)
      if (index < 0) return
      setModified(true)
      setState(evolve({ items: remove(index, 1) }))
    },
    [state.items, setState],
  )

  /**
   *  Полная стоимость заказа (без учета бесплатных)
   */

  const orderTotal = useMemo(() => {
    if (!state.items) return 0

    return pipe(
      map(({ cost, n }) => cost * n),
      sum,
    )(state.items)
  }, [state.items])

  /**
   *  Полная стоимость заказа учитывая бесплатные комплекты
   *  Из полной стоимости вычитается стоимость бесплатных
   */

  const orderTotalConsideringFreeItems = useMemo(
    () =>
      reduce(
        (acc, { cost, free_for_sale: free }) => {
          // бесплатные комплекты не учитыаются (первый в заказе)
          if (free) return acc - cost
          return acc
        },
        orderTotal,
        state.items || [],
      ),
    [orderTotal, state.items],
  )

  /**
   *  Количество блюд или комплектов по категории
   */

  const countByCategoryId = useMemo(
    () =>
      reduce(
        (acc, { guid }) => {
          const item = dishes[guid] || complexes[guid]
          if (!item) return acc
          const categoryId = item.category_id || 'complexes'
          acc[categoryId] = acc[categoryId] ? acc[categoryId] + 1 : 1
          return acc
        },
        {},
        state.items || [],
      ),
    [state.items, dishes, complexes],
  )

  useEffect(() => {
    lockControls.current = false
  }, [orderTotalConsideringFreeItems])

  const contextValue: OrderDataContextType = useMemo(
    () => ({
      order: state,
      orderTotal,
      orderTotalConsideringFreeItems,
      addToOrder,
      setOrder,
      initOrder,
      isItemInOrder,
      itemCountIncrement,
      itemCountDecrement,
      itemRemove,
      modified,
      someItemsRemoved,
      somePricesChanged,
      countByCategoryId,
      savedOrder,
      additionalFreeGuids,
      setAdditionalFreeGuids,
    }),
    [
      state,
      orderTotalConsideringFreeItems,
      setOrder,
      initOrder,
      addToOrder,
      isItemInOrder,
      orderTotal,
      itemCountIncrement,
      itemCountDecrement,
      itemRemove,
      modified,
      someItemsRemoved,
      somePricesChanged,
      countByCategoryId,
      savedOrder,
      additionalFreeGuids,
      setAdditionalFreeGuids,
    ],
  )

  return (
    <OrderDataContext.Provider value={contextValue}>
      {typeof children === 'function' ? children(contextValue) : children}
    </OrderDataContext.Provider>
  )
}

export default OrderData
